"use client";
import { jsx, jsxs } from "react/jsx-runtime";
import { DatePicker, FilterChip, InputChip, Spinner, TravelerSelection } from "@pcln/horizon";
import { forwardRef, useMemo } from "react";
import { HOTEL_MAX_CHILDREN, HOTEL_MAX_ROOMS, HOTEL_MAX_TRAVELERS } from "../../hotels/hotelTravelerLimits.js";
import { useAiSearchDateChip } from "../hooks/useAiSearchDateChip.js";
import { AiSearchCityChip } from "./AiSearchCityChip.js";
const CHIP_ANIMATION = 'animate-fade-in-left-sm motion-reduce:animate-none';
const DATE_POPOVER_HEADING = 'Hotels: Check-in - Check-out';
const FILTER_CHIP_TRIGGER = {
    opens: false,
    type: 'chip',
    emphasis: 'regular',
    showCheckIcon: false
};
const AiSearchFilterChips_ChipTrigger = /*#__PURE__*/ forwardRef(function({ chipLabel, iconLeft, isOpen, onClick, onMouseDown, ...triggerProps }, ref) {
    return /*#__PURE__*/ jsx(FilterChip, {
        ...triggerProps,
        ...FILTER_CHIP_TRIGGER,
        ref: ref,
        className: "openable-filter-chip",
        label: chipLabel,
        iconLeft: iconLeft,
        selected: isOpen,
        onClick: onClick,
        onMouseDown: onMouseDown
    });
});
function getTravelerRemountKey(travelers) {
    const childrenAges = Object.entries(travelers.childrenAges ?? {}).map(([index, age])=>`${index}:${age}`).sort().join(',');
    return [
        travelers.adults,
        travelers.children,
        travelers.rooms,
        travelers.lapInfants,
        childrenAges
    ].join('|');
}
function AiSearchFilterChips({ startDate, endDate, travelers, onDatesChange, onTravelersChange, city, renderCityAutocomplete, onCityChange, removableFilters = [], onRemoveFilter, isLoading = false }) {
    const { dateChipLabel, datePickerProps } = useAiSearchDateChip({
        startDate,
        endDate,
        onDatesChange
    });
    const travelerRemountKey = useMemo(()=>getTravelerRemountKey(travelers), [
        travelers
    ]);
    return /*#__PURE__*/ jsxs("div", {
        className: "flex flex-wrap items-center gap-2",
        "data-testid": "ai-search-filter-chips",
        "aria-busy": isLoading,
        children: [
            /*#__PURE__*/ jsx("div", {
                className: CHIP_ANIMATION,
                "data-testid": "ai-search-date-chip",
                children: /*#__PURE__*/ jsx(DatePicker, {
                    ...datePickerProps,
                    mode: "range",
                    label: DATE_POPOVER_HEADING,
                    heading: DATE_POPOVER_HEADING,
                    subHeading: dateChipLabel,
                    numberOfMonths: 2,
                    min: 1,
                    renderTrigger: (triggerProps)=>/*#__PURE__*/ jsx(AiSearchFilterChips_ChipTrigger, {
                            ...triggerProps,
                            chipLabel: dateChipLabel,
                            iconLeft: "date_range"
                        })
                })
            }),
            /*#__PURE__*/ jsx("div", {
                className: CHIP_ANIMATION,
                "data-testid": "ai-search-traveler-chip",
                children: /*#__PURE__*/ jsx(TravelerSelection, {
                    isHotel: true,
                    roomsEnabled: true,
                    ageSelectEnabled: true,
                    maxRooms: HOTEL_MAX_ROOMS,
                    maxChildren: HOTEL_MAX_CHILDREN,
                    maxTravelers: HOTEL_MAX_TRAVELERS,
                    initialValues: travelers,
                    tooltipErrorMessageId: "ai-search-traveler-selection-error",
                    onChange: onTravelersChange,
                    renderTrigger: ({ value, ...triggerProps })=>/*#__PURE__*/ jsx(AiSearchFilterChips_ChipTrigger, {
                            ...triggerProps,
                            chipLabel: value,
                            iconLeft: "group"
                        })
                }, travelerRemountKey)
            }),
            city && renderCityAutocomplete ? /*#__PURE__*/ jsx("div", {
                className: CHIP_ANIMATION,
                children: /*#__PURE__*/ jsx(AiSearchCityChip, {
                    city: city,
                    renderAutocomplete: renderCityAutocomplete,
                    onCityChange: onCityChange
                })
            }) : null,
            removableFilters.map((filter)=>/*#__PURE__*/ jsx("div", {
                    className: CHIP_ANIMATION,
                    "data-testid": `ai-search-removable-chip-${filter.id}`,
                    children: /*#__PURE__*/ jsx(InputChip, {
                        label: filter.label,
                        onCloseButtonClick: ()=>{
                            onRemoveFilter?.(filter.id);
                        }
                    })
                }, filter.id)),
            isLoading ? /*#__PURE__*/ jsx("div", {
                role: "status",
                "aria-label": "Loading search results",
                "data-testid": "ai-search-filter-chips-spinner",
                children: /*#__PURE__*/ jsx(Spinner, {
                    size: "sm",
                    isLoading: true,
                    gradientId: "ai-search-filter-chips-spinner"
                })
            }) : null
        ]
    });
}
export { AiSearchFilterChips };
